import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import { CreditCard, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';

const AdminBilling = () => {
    const { slug } = useParams();
    const [statusData, setStatusData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [mpEmail, setMpEmail] = useState('');
    const [isSyncing, setIsSyncing] = useState(false);

    const fetchStatus = async (showSync = false) => {
        if (showSync) setIsSyncing(true);
        try {
            const res = await axios.get(`/${slug}/api/subscriptions/status`, { withCredentials: true });
            setStatusData(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
            setIsSyncing(false);
        }
    };

    useEffect(() => {
        fetchStatus();
        
        // Polling logic: Si acabamos de volver de MP o estamos en un estado de transición
        const query = new URLSearchParams(window.location.search);
        const returningFromMP = query.has('preapproval_id') || query.has('status');
        
        let interval;
        if (returningFromMP) {
            // Poll cada 3 segundos durante 15 segundos max o hasta que sea active
            let attempts = 0;
            interval = setInterval(() => {
                fetchStatus();
                attempts++;
                if (attempts > 5) clearInterval(interval);
            }, 3000);
        }
        
        return () => clearInterval(interval);
    }, [slug]);

    useEffect(() => {
        // Si el estado cambia a activo, limpiamos la URL de parámetros de MP para que no siga haciendo polling al recargar
        if (statusData?.subscriptionStatus === 'active') {
            const query = new URLSearchParams(window.location.search);
            if (query.has('preapproval_id') || query.has('status')) {
                window.history.replaceState({}, document.title, window.location.pathname);
            }
        }
    }, [statusData?.subscriptionStatus]);

    const handleSubscribe = async () => {
        if (!mpEmail || !mpEmail.includes('@')) {
            alert('Debes escribir el correo electrónico exacto que usas en MercadoPago.');
            return;
        }
        setActionLoading(true);
        try {
            const res = await axios.post(`/${slug}/api/subscriptions/create`, { payer_email: mpEmail }, { withCredentials: true });
            if (res.data.init_point) {
                window.location.href = res.data.init_point;
            }
        } catch (err) {
            console.error(err);
            alert('Error al generar el link de suscripción. Intente más tarde.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancel = async () => {
        if (!window.confirm("¿Estás seguro/a que deseas cancelar tu suscripción a PASSAR? Ya no podrás generar tickets VIP ni efectivos gratuitos.\n\nAl hacerlo volverás a estar en el plan por comisiones.")) return;
        
        setActionLoading(true);
        try {
            const res = await axios.post(`/${slug}/api/subscriptions/cancel`, {}, { withCredentials: true });
            if (res.data.success) {
                alert("Suscripción cancelada exitosamente.");
                // Recargar el estado
                window.location.reload();
            }
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.error || 'Error al cancelar la suscripción. Intente nuevamente.');
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) return <div className="p-8 text-center px-4 md:p-8 max-w-4xl mx-auto text-white/50"><Loader2 className="animate-spin inline mr-2" />Cargando facturación...</div>;

    const { billingModel, subscriptionStatus, subscriptionNextBillingDate } = statusData || {};
    const actualBillingModel = billingModel || 'commission';
    const actualSubscriptionStatus = subscriptionStatus || 'none';

    return (
        <div className="p-4 pt-24 md:p-8 max-w-4xl mx-auto text-white">
            <h1 className="text-2xl font-bold mb-6 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <CreditCard className="text-primary-orange" />
                    Facturación y Planes
                </div>
                <button 
                    onClick={() => fetchStatus(true)} 
                    disabled={isSyncing || loading}
                    className="text-xs bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all border border-white/5"
                >
                    <Loader2 className={`w-3 h-3 ${isSyncing ? 'animate-spin' : ''}`} />
                    {isSyncing ? 'Sincronizando...' : 'Actualizar Estado'}
                </button>
            </h1>

            {new URLSearchParams(window.location.search).has('preapproval_id') && statusData?.subscriptionStatus !== 'active' && (
                <div className="mb-6 bg-blue-500/10 border border-blue-500/30 rounded-2xl p-4 flex items-center gap-4 animate-pulse">
                    <Loader2 className="animate-spin text-blue-400" size={20} />
                    <p className="text-sm text-blue-200">Verificando tu suscripción con MercadoPago, por favor aguarda unos instantes...</p>
                </div>
            )}

            {actualBillingModel === 'commission' ? (
                 <div className="bg-[#18181b] border border-white/10 rounded-3xl p-6 md:p-8 text-center flex flex-col items-center">
                    <h2 className="text-lg md:text-xl font-bold mb-4">Modelo Actual: Comisión por Venta</h2>
                    <p className="text-white/60 mb-6 max-w-lg mx-auto text-sm md:text-base">
                        Actualmente utilizas PASSAR bajo el modelo estándar de organizador. Cobramos una comisión por cada entrada vendida online a través de MercadoPago.
                    </p>
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mt-4 w-full max-w-lg">
                        <h3 className="text-lg font-bold mb-2">¿Quieres pasarte al Plan SaaS?</h3>
                        {!statusData?.hasUsedFreeTrial && (
                            <div className="bg-green-500/10 border border-green-500/30 text-green-400 p-3 rounded-xl mb-4 text-sm font-bold w-full max-w-lg mt-2 text-center mx-auto">
                                🎉 ¡Aprovecha tus primeros 90 días GRATIS! MercadoPago no te cobrará nada hoy. Suscríbete ahora para activarlos.
                            </div>
                        )}
                        <p className="text-sm text-white/50 mb-6 focus:outline-none">
                            Ideal para Salones de Eventos. Paga una cuota fija de $100 ARS/mes (PRECIO PROMOCIONAL TESTING) y habilita la emisión manual ilimitada para tus pases cobrados en efectivo o invitaciones.
                        </p>
                        
                        <div className="mb-6 text-left">
                            <label className="block text-xs font-bold text-white/40 uppercase mb-2">Tu Cuenta de MercadoPago</label>
                            <input 
                                type="email" 
                                placeholder="tu-email@mercadopago.com"
                                value={mpEmail}
                                onChange={(e) => setMpEmail(e.target.value)}
                                className="w-full bg-[#1A1A1A] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#009EE3]"
                                required
                            />
                            <p className="text-[10px] text-white/30 mt-2 mt-1">
                                Escribe exactamente el mail con el que iniciarás sesión en MercadoPago para pagar. Si no coinciden, la plataforma rechazará el pago.
                            </p>
                        </div>

                        <button
                            onClick={handleSubscribe}
                            disabled={actionLoading || !mpEmail}
                            className="px-6 py-3 bg-[#009EE3] hover:bg-[#0089C7] text-white font-bold rounded-xl transition-all shadow-lg flex items-center justify-center w-full mx-auto gap-2 disabled:opacity-50"
                        >
                            {actionLoading ? <Loader2 className="animate-spin" size={18} /> : null}
                            Suscribirme y Cambiar de Plan
                        </button>
                    </div>
                 </div>
            ) : (
                <div className="bg-[#18181b] border border-white/10 rounded-3xl p-6 md:p-8">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 border-b border-white/10 pb-6 mb-6">
                         <div>
                             <h2 className="text-xl font-bold mb-1">Plan SaaS (Salón de Eventos)</h2>
                             <p className="text-white/50 text-sm">Acceso irrestricto a la generación de entradas manuales en efectivo e invitaciones gratuitas ilimitadas para tus clientes.</p>
                         </div>
                         <div className="text-left md:text-right">
                             <div className="text-2xl font-black text-white">$100 ARS<span className="text-sm text-white/40 font-normal"> /mes</span></div>
                         </div>
                    </div>

                    {/* Estados */}
                    {actualSubscriptionStatus === 'active' && (
                        <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-green-500/20 rounded-xl text-green-400">
                                    <CheckCircle size={24} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-green-400">Suscripción Activa</h3>
                                    <p className="text-xs text-white/60 mt-1">
                                        Próxima facturación: {subscriptionNextBillingDate ? new Date(subscriptionNextBillingDate).toLocaleDateString() : 'Pendiente o Recurrente'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="px-4 py-2 bg-white/5 rounded-lg text-sm font-semibold text-white/70">Mensual</div>
                                <button
                                    onClick={handleCancel}
                                    disabled={actionLoading}
                                    className="px-4 py-2 text-xs border border-red-500/30 hover:bg-red-500/20 text-red-400 rounded-lg transition-all shadow-sm flex items-center gap-2 disabled:opacity-50"
                                    title="Darse de baja de la suscripción mensual"
                                >
                                    {actionLoading ? <Loader2 className="animate-spin" size={12} /> : null}
                                    Cancelar Suscripción
                                </button>
                            </div>
                        </div>
                    )}

                    {actualSubscriptionStatus === 'past_due' && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 flex flex-col md:flex-row items-center gap-4 justify-between">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-red-500/20 rounded-xl text-red-500">
                                    <AlertTriangle size={24} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-red-500">Pago Rechazado o Vencido</h3>
                                    <p className="text-xs text-red-400/80 mt-1">El último cobro de tu tarjeta rebotó o te quedaste sin límite. Tu sistema de emisión de QRs ha sido temporalmente bloqueado.</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleSubscribe}
                                    disabled={actionLoading}
                                    className="px-5 py-2.5 bg-[#009EE3] hover:bg-[#0089C7] text-white text-sm font-bold rounded-xl transition-all shadow-lg flex items-center shrink-0 whitespace-nowrap disabled:opacity-50"
                                >
                                    {actionLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                                    Rastrear Deuda / Reintentar
                                </button>
                                <button
                                    onClick={handleCancel}
                                    disabled={actionLoading}
                                    className="px-4 py-2.5 border border-red-500/50 hover:bg-red-500/20 text-red-400 text-sm font-bold rounded-xl transition-all disabled:opacity-50"
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    )}

                    {(actualSubscriptionStatus === 'none' || actualSubscriptionStatus === 'cancelled') && (
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-10 text-center flex flex-col items-center">
                            <h3 className="text-lg font-bold mb-2">Aún no tienes un plan mensual activo</h3>
                            {!statusData?.hasUsedFreeTrial && (
                                <div className="bg-green-500/10 border border-green-500/30 text-green-400 p-3 rounded-xl mb-4 mt-2 text-sm font-bold w-full max-w-sm">
                                    🎉 Tienes 90 días GRATIS. ¡Regístrate ahora sin pagar nada hoy!
                                </div>
                            )}
                            <p className="text-sm text-white/50 mb-6 max-w-md mx-auto">
                                Suscríbete ahora de manera segura usando MercadoPago Suscripciones para desbloquear la consola de emisión manual de pases VIP y boletos pagados en efectivo de tu salón.
                            </p>

                            <div className="mb-6 text-left w-full max-w-sm">
                                <label className="block text-xs font-bold text-white/40 uppercase mb-2">Email de tu Cuenta MercadoPago</label>
                                <input 
                                    type="email" 
                                    placeholder="tu-email@mercadopago.com"
                                    value={mpEmail}
                                    onChange={(e) => setMpEmail(e.target.value)}
                                    className="w-full bg-[#1A1A1A] border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#009EE3]"
                                    required
                                />
                                <p className="text-[10px] text-white/30 mt-2">
                                    Asegúrate de poner aquí exactamente el correo con el que abrirás sesión en MercadoPago en el próximo paso, de lo contrario será rechazado.
                                </p>
                            </div>

                            <button
                                onClick={handleSubscribe}
                                disabled={actionLoading || !mpEmail}
                                className="px-8 py-4 bg-[#009EE3] hover:bg-[#0089C7] text-white font-bold rounded-xl transition-all shadow-lg flex items-center justify-center w-full max-w-sm mx-auto gap-2 disabled:opacity-50"
                            >
                                {actionLoading ? <Loader2 className="animate-spin" size={20} /> : <CreditCard size={20} />}
                                Suscribirme de manera segura
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminBilling;
